import React, { useState } from 'react';
import { Modal, Select, Button, message } from 'antd';
import { contractsApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

interface CloneContractModalProps {
  visible: boolean;
  onCancel: () => void;
  onCloneSuccess: (newContractRef: string) => void;
}

export const CloneContractModal: React.FC<CloneContractModalProps> = ({
  visible,
  onCancel,
  onCloneSuccess
}) => {
  const [selectedContract, setSelectedContract] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<any[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  const loadContracts = async () => {
    setLoadingContracts(true);
    try {
      const response: any = await contractsApi.getContracts({
        limit: 50
      });
      // Axios interceptor unwraps response.data, so access directly
      setContracts(response.contracts || []);
    } catch (error) {
      console.error('Failed to load contracts:', error);
      message.error('Failed to load contracts');
    } finally {
      setLoadingContracts(false);
    }
  };

  React.useEffect(() => {
    if (visible) {
      loadContracts();
    }
  }, [visible]);

  const handleClone = async () => {
    if (!selectedContract) {
      message.error('Please select a contract to clone');
      return;
    }

    setLoading(true);
    try {
      // Get the original contract details
      // Axios interceptor unwraps response.data, so access directly
      const contractData: any = await contractsApi.getContract(selectedContract);

      // Create new contract with cloned data
      const newContractData = {
        supplier_code: contractData.supplier?.code,
        buyer_code: contractData.buyer?.code,
        contract_date: new Date().toISOString().split('T')[0],
        crop_year: new Date().getFullYear().toString().slice(-2),
        terms: [
          {
            type: 'PAYMENT' as const,
            description: `Cloned from ${contractData.reference}`,
            value: 'Standard'
          }
        ]
      };

      const response: any = await contractsApi.createContract(newContractData);
      message.success(`Contract cloned successfully: ${response.reference}`);
      onCloneSuccess(response.reference);
    } catch (error: any) {
      console.error('Failed to clone contract:', error);
      message.error(error.response?.data?.error || 'Failed to clone contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Clone Contract from Existing"
      visible={visible}
      onCancel={onCancel}
      onOk={handleClone}
      okText="Clone Contract"
      okButtonProps={{ disabled: !selectedContract, loading }}
      cancelButtonProps={{ disabled: loading }}
      width={600}
    >
      <div>
        <p>Select an existing contract to use as a template for the new contract:</p>
        <Select
          style={{ width: '100%', marginTop: 16 }}
          placeholder="Select a contract to clone"
          loading={loadingContracts}
          onChange={setSelectedContract}
          value={selectedContract}
          showSearch
          optionFilterProp="children"
        >
          {contracts.map((contract) => (
            <Option key={contract.reference} value={contract.reference}>
              {contract.reference} - {contract.supplier?.shortName || contract.supplier?.name} → {contract.buyer?.shortName || contract.buyer?.name}
            </Option>
          ))}
        </Select>
      </div>
    </Modal>
  );
};